import React, { useState, useEffect } from 'react';
import {
  Box, Card, CardContent, Typography, TextField, MenuItem,
  CircularProgress, Grid, Chip,
} from '@mui/material';
import { School } from '@mui/icons-material';
import { InvokeApi, apiList } from '../services/ApiService';
import { Config } from '../config/Config';

export default function ClassSectionGrid({
  onSelect,
  badge,
  subtitle,
  acYear: controlledAcYear,
  onAcYearChange,
}) {
  const [sections, setSections] = useState([]);
  const [academicYears, setAcademicYears] = useState([]);
  const [selectedAcYear, setSelectedAcYear] = useState(controlledAcYear || '');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      const [secData, acData] = await Promise.all([
        InvokeApi(Config.main_url + apiList.getDeptartmentSection, { status: 'Active' }),
        InvokeApi(Config.main_url + apiList.getAvailableAcademicYears, {}),
      ]);
      setSections(secData?.sections || []);
      const years = acData?.academic_years || [];
      setAcademicYears(years);
      if (!controlledAcYear) {
        const current = years.find(y => y.is_current) || years[0];
        if (current) setSelectedAcYear(current.value || '');
      }
      setLoading(false);
    };
    init();
  }, []);

  useEffect(() => {
    if (controlledAcYear) setSelectedAcYear(controlledAcYear);
  }, [controlledAcYear]);

  const handleYearChange = (val) => {
    setSelectedAcYear(val);
    onAcYearChange?.(val);
  };

  const handleClick = (cls) => {
    onSelect?.({ standard: cls.standard.trim(), section: cls.section.trim(), acYear: selectedAcYear });
  };

  const grouped = sections.reduce((acc, cls) => {
    const std = cls.standard.trim();
    if (!acc[std]) acc[std] = [];
    acc[std].push(cls);
    return acc;
  }, {});

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <TextField select size="small" label="Academic Year" value={selectedAcYear}
          onChange={e => handleYearChange(e.target.value)} sx={{ minWidth: 180 }}>
          {academicYears.map(y => <MenuItem key={y.value} value={y.value}>{y.label}</MenuItem>)}
        </TextField>
        <Typography variant="body2" color="text.secondary">
          {sections.length} classes · Click to view details
        </Typography>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 8 }}>
          <CircularProgress sx={{ color: '#1a237e' }} />
        </Box>
      ) : sections.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <School sx={{ fontSize: 64, color: '#e8eaf6', mb: 2 }} />
          <Typography color="text.secondary" fontWeight={500}>No classes found</Typography>
          <Typography variant="caption" color="text.secondary">Add classes in Departments section</Typography>
        </Box>
      ) : <Grid container spacing={4} sx={{display: 'flex', flexDirection: 'row', alignItems: 'flex-start', justifyContent: 'flex-start'}}>
        {
          Object.entries(grouped).map(([std, classes]) => (
            <Grid key={std} mb={4} item xs={12} sm={12} md={4} lg={3}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                <Box sx={{ width: 4, height: 20, borderRadius: 2, background: 'linear-gradient(135deg,#1a237e,#1565c0)' }} />
                <Typography fontWeight={800} fontSize={15} color="#1a237e">{std}</Typography>
                <Chip label={`${classes.length} section${classes.length > 1 ? 's' : ''}`} size="small"
                  sx={{ background: '#e8eaf6', color: '#1a237e', fontWeight: 600, fontSize: 10 }} />
              </Box>

              <Grid>
                {classes.map((cls, i) => {
                  const b = badge?.(cls);
                  const sub = subtitle?.(cls);
                  return (
                    <Grid key={i}>
                      <Card onClick={() => handleClick(cls)} sx={{
                        borderRadius: 3, cursor: 'pointer',
                        boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
                        transition: 'all 0.2s',
                        border: '1.5px solid #e8eaf6',
                        '&:hover': {
                          transform: 'translateY(-3px)',
                          boxShadow: '0 8px 24px rgba(26,35,126,0.18)',
                          borderColor: '#1a237e',
                        },
                      }}>
                        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                            <Box sx={{
                              width: 40, height: 40, borderRadius: 2,
                              background: 'linear-gradient(135deg,#1a237e,#1565c0)',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                            }}>
                              <Typography sx={{ color: '#fff', fontWeight: 900, fontSize: 16 }}>
                                {cls.section.trim().slice(0, 3)}
                              </Typography>
                            </Box>
                            {b && (
                              <Chip label={b.label} size="small"
                                sx={{ background: b.bg || '#e8eaf6', color: b.color || '#1a237e', fontWeight: 700, fontSize: 10 }} />
                            )}
                          </Box>

                          <Typography fontWeight={700} fontSize={13} color="#1a237e" noWrap>
                            {std} - {cls.section.trim()}
                          </Typography>

                          {sub && (
                            <Typography fontSize={11} color="text.secondary" mt={0.3} noWrap>{sub}</Typography>
                          )}
                        </CardContent>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            </Grid>
          ))
        }
      </Grid>}
    </Box>
  );
}
